export enum PointOfCareTerminologyVariant {
	Hcc = 'Hcc',
	NonHcc = 'NonHcc',
}

export interface IDiagnosesTerminologyModel {
	readonly tabPageHeaderSubtitle: string;
	readonly hccGapLabel: string;
	readonly hccGroupAddressedLabel: string;
	readonly hccGroupAddressedSyncedSameLabel: string;
	readonly hccGroupAddressedSyncedLowerLabel: string;
	readonly hccGroupAddressedLowerRankedLabel: string;
	readonly hccAddressedInEhrLabel: string;
	readonly multipleHccsLabel: string;
	readonly differentHccLabel: string;
	readonly noHccLabel: string;
	/** Prefix before a companion (secondary) diagnosis code in its card header, e.g. "Coded with N18.31, …". */
	readonly companionDiagnosisPrefix: string;
	readonly differentHccGroupSuffixLabel: (hccGroup: string) => string;
	readonly addressedHccGroupSuffixLabel: (hccGroup: string) => string;
	readonly differentHccGroupsSuffixLabel: (hccGroups: string) => string;
	readonly addressedHccGroupsSuffixLabel: (hccGroups: string) => string;
	readonly dualHccTagLabel: (secondaryHccGroups: string) => string;
	readonly gapsAddressedLabel: string;
	readonly scheduleHccGapsColumnTitle: string;
	readonly showHccRiskModelSubtitle: boolean;
	readonly showRiskModelInformation: boolean;
	readonly optionalLabelTooltip: string;
	readonly preferActionDescription: boolean;
	readonly hccGapCountLabel: (count: number) => string;
	readonly hccGroupAddressedCountLabel: (count: number) => string;
	readonly hccGroupAddressedSyncedSameCountLabel: (count: number) => string;
	readonly hccGroupAddressedSyncedLowerCountLabel: (count: number) => string;
	readonly hccGroupAddressedLowerRankedCountLabel: (count: number) => string;
	readonly hccAddressedInEhrCountLabel: (count: number) => string;
	readonly diagnosisAlreadyAddressedGroupNote: (group: string) => string;
	readonly diagnosisAlreadyAddressedGroupsNote: (groups: string) => string;
	readonly diagnosisLowerRankedGroupNote: (group: string) => string;
	readonly hccGroupSortOptionLabel: string;
	readonly showHccGroupTagPrefix: boolean;
	readonly hccGroupPrefixLabel: (primaryGroup: string, allGroups: ReadonlyArray<string>) => string;
	/** Whether a dual-code card shows the "dual HCC (…)" flag tag. Off for variants that don't surface it. */
	readonly showDualHccTag: boolean;
	readonly hccGroupFallbackName: (group: string) => string;
	readonly hccLowerRankedHierarchyTooltip: (currentGroup: string, higherAddressedGroup: string) => string;
	readonly hccTagTooltipLine?: (group: string, riskModelVersion: string, weight: number) => string;
	readonly autoCloseSameGroupReason: string;
	readonly autoCloseHigherGroupReason: string;
	readonly rafCapturedItemsLabel: string;
}

const HCC_DIFFERENT_HCC_LABEL = 'Different HCC';

const HCC_TERMINOLOGY_CONFIG = {
	tabPageHeaderSubtitle: 'Review evidence-based suggestions to close HCC gaps',
	hccGapLabel: 'HCC gap',
	scheduleHccGapsColumnTitle: 'HCC Gaps',
	hccGroupAddressedLabel: 'HCC group addressed',
	hccGroupAddressedSyncedSameLabel: 'HCC group addressed - diagnosis synced (same HCC)',
	hccGroupAddressedSyncedLowerLabel: 'HCC group addressed - diagnosis synced (lower HCC)',
	hccGroupAddressedLowerRankedLabel: 'HCC group addressed (lower ranked HCC)',
	hccAddressedInEhrLabel: 'HCC addressed in EHR',
	multipleHccsLabel: 'Multiple HCCs',
	differentHccLabel: HCC_DIFFERENT_HCC_LABEL,
	noHccLabel: 'No HCC',
	companionDiagnosisPrefix: 'Coded with',
	differentHccGroupSuffixLabel: (hccGroup: string): string => `${HCC_DIFFERENT_HCC_LABEL} (${hccGroup})`,
	addressedHccGroupSuffixLabel: (hccGroup: string): string => `HCC ${hccGroup} addressed`,
	differentHccGroupsSuffixLabel: (hccGroups: string): string => `${HCC_DIFFERENT_HCC_LABEL}s (${hccGroups})`,
	addressedHccGroupsSuffixLabel: (hccGroups: string): string => `HCCs ${hccGroups} addressed`,
	dualHccTagLabel: (secondaryHccGroups: string): string => `dual HCC (${secondaryHccGroups})`,
	gapsAddressedLabel: 'HCC gaps addressed',
	showHccRiskModelSubtitle: true,
	showRiskModelInformation: true,
	optionalLabelTooltip: 'This HCC group was already addressed',
	preferActionDescription: false,
	hccGapCountLabel: (count: number): string => `${count} HCC Gap${count === 1 ? '' : 's'}`,
	hccGroupAddressedCountLabel: (count: number): string => `HCC group${count === 1 ? '' : 's'} addressed`,
	hccGroupAddressedSyncedSameCountLabel: (count: number): string =>
		`HCC group${count === 1 ? '' : 's'} addressed - diagnosis synced (same HCC)`,
	hccGroupAddressedSyncedLowerCountLabel: (count: number): string =>
		`HCC group${count === 1 ? '' : 's'} addressed - diagnosis synced (lower HCC)`,
	hccGroupAddressedLowerRankedCountLabel: (count: number): string =>
		`HCC group${count === 1 ? '' : 's'} addressed (lower ranked HCC)`,
	hccAddressedInEhrCountLabel: (count: number): string => `HCC${count === 1 ? '' : 's'} addressed in EHR`,
	diagnosisAlreadyAddressedGroupNote: (group: string): string =>
		`This diagnosis is from HCC group ${group} which was already addressed in the current reporting period.`,
	diagnosisAlreadyAddressedGroupsNote: (groups: string): string =>
		`This diagnosis is from HCC groups ${groups} which were already addressed in the current reporting period.`,
	diagnosisLowerRankedGroupNote: (group: string): string =>
		`This diagnosis is from HCC group ${group}}, another ICD from a higher HCC group was already documented this year.`,
	hccGroupSortOptionLabel: 'HCC Group',
	showHccGroupTagPrefix: true,
	hccGroupPrefixLabel: (primaryGroup: string, allGroups: ReadonlyArray<string>): string =>
		allGroups.length > 1 ? `HCC ${allGroups.join(', ')}` : `HCC${primaryGroup}`,
	showDualHccTag: true,
	hccGroupFallbackName: (group: string): string => `HCC Group ${group}`,
	hccLowerRankedHierarchyTooltip: (currentGroup: string, higherAddressedGroup: string): string =>
		`HCC ${currentGroup} defined as lower in hierarchy vs HCC ${higherAddressedGroup} and won't impact risk adjustment`,
	hccTagTooltipLine: (group: string, riskModelVersion: string, weight: number): string =>
		`HCC ${group} (${riskModelVersion}, ${weight})`,
	autoCloseSameGroupReason: 'acceptance of a different diagnosis from the same HCC group',
	autoCloseHigherGroupReason: 'acceptance of a different diagnosis from a higher HCC group',
	rafCapturedItemsLabel: 'Captured HCCs',
} as const satisfies IDiagnosesTerminologyModel;

const NON_HCC_DIFFERENT_CONDITION_LABEL = 'Different unaddressed condition';

const NON_HCC_TERMINOLOGY_CONFIG = {
	tabPageHeaderSubtitle: 'Unaddressed chronic conditions',
	hccGapLabel: 'Unaddressed',
	hccGroupAddressedLabel: 'Addressed',
	scheduleHccGapsColumnTitle: 'Unaddressed Conditions',
	hccGroupAddressedSyncedSameLabel: 'Addressed - diagnosis synced (same condition)',
	hccGroupAddressedSyncedLowerLabel: 'Addressed - diagnosis synced (lower condition)',
	hccGroupAddressedLowerRankedLabel: 'Addressed (lower ranked condition)',
	hccAddressedInEhrLabel: 'Addressed in EHR',
	multipleHccsLabel: 'Multiple unaddressed conditions',
	differentHccLabel: NON_HCC_DIFFERENT_CONDITION_LABEL,
	noHccLabel: 'No condition',
	companionDiagnosisPrefix: 'Diagnosed with',
	differentHccGroupSuffixLabel: (_hccGroup: string): string => 'Different condition group',
	addressedHccGroupSuffixLabel: (_hccGroup: string): string => `Condition group addressed`,
	differentHccGroupsSuffixLabel: (_hccGroups: string): string => 'Different condition groups',
	addressedHccGroupsSuffixLabel: (_hccGroups: string): string => `Condition groups addressed`,
	dualHccTagLabel: (_secondaryHccGroups: string): string => `dual condition`,
	gapsAddressedLabel: 'Chronic Conditions Addressed',
	showHccRiskModelSubtitle: false,
	showRiskModelInformation: false,
	optionalLabelTooltip: 'This condition group was already addressed',
	preferActionDescription: true,
	hccGapCountLabel: (count: number): string => `${count} unaddressed condition${count === 1 ? '' : 's'}`,
	hccGroupAddressedCountLabel: (count: number): string => `${count} condition${count === 1 ? '' : 's'} addressed`,
	hccGroupAddressedSyncedSameCountLabel: (count: number): string =>
		`Addressed - diagnosis synced (same condition${count === 1 ? '' : 's'})`,
	hccGroupAddressedSyncedLowerCountLabel: (count: number): string =>
		`Addressed - diagnosis synced (lower condition${count === 1 ? '' : 's'})`,
	hccGroupAddressedLowerRankedCountLabel: (count: number): string =>
		`Addressed (lower ranked condition${count === 1 ? '' : 's'})`,
	hccAddressedInEhrCountLabel: (_count: number): string => `Addressed in EHR`,
	diagnosisAlreadyAddressedGroupNote: (_group: string): string =>
		`This diagnosis is from condition group that was already addressed in the current reporting period.`,
	diagnosisAlreadyAddressedGroupsNote: (_addressedSectionSortingKeygroups: string): string =>
		`This diagnosis is from condition groups that were already addressed in the current reporting period.`,
	diagnosisLowerRankedGroupNote: (_group: string): string =>
		`This diagnosis is from condition group, another ICD from a higher priority condition group was already documented this year.`,
	hccGroupSortOptionLabel: 'Group',
	showHccGroupTagPrefix: false,
	hccGroupPrefixLabel: (_primaryGroup: string, allGroups: ReadonlyArray<string>): string =>
		allGroups.length > 1 ? 'Conditions' : 'Condition',
	showDualHccTag: false,
	hccGroupFallbackName: (_group: string): string => `Condition group`,
	hccLowerRankedHierarchyTooltip: (_currentGroup: string, _higherAddressedGroup: string): string =>
		`Condition group defined as lower in hierarchy vs condition group that was already addressed and won't impact risk adjustment`,
	autoCloseSameGroupReason: 'acceptance of a different diagnosis from the same condition group',
	autoCloseHigherGroupReason: 'acceptance of a different diagnosis from a higher priority condition group',
	rafCapturedItemsLabel: 'Captured conditions',
} as const satisfies IDiagnosesTerminologyModel;

export const DIAGNOSES_TERMINOLOGY_BY_VARIANT: Readonly<
	Record<PointOfCareTerminologyVariant, IDiagnosesTerminologyModel>
> = {
	[PointOfCareTerminologyVariant.Hcc]: HCC_TERMINOLOGY_CONFIG,
	[PointOfCareTerminologyVariant.NonHcc]: NON_HCC_TERMINOLOGY_CONFIG,
};

export const DEFAULT_TERMINOLOGY_VARIANT = PointOfCareTerminologyVariant.Hcc;
export const DEFAULT_DIAGNOSES_TERMINOLOGY: IDiagnosesTerminologyModel = HCC_TERMINOLOGY_CONFIG;
